import * as API from 'aws-amplify/api';
import { ConsoleLogger as Logger } from 'aws-amplify/utils';
import { groupBy, merge } from 'lodash';
import { notification } from 'antd';
import { createErrorMessage } from './globalStateHelper';
import { getEmptyData } from '../utils/capacityAndNominationsHelper';
import { getDatesInRange } from '../utils/dateTimeHelpers';

const log = new Logger('context:global:monitoring');

async function updateTechnicalCapacity(start, end, resolution) {
  try {
    const technicalCapacity = API.get({
      apiName: 'FINTSO',
      path: '/statistics/capacities/technical/bc',
      options: {
        queryParams: {
          start,
          end,
          resolution,
        },
      },
    });
    this.setState({ technicalCapacity });
    log.info('Technical capacity updated with data:', technicalCapacity);
  } catch (error) {
    const { t } = this.props;
    this.setState({ technicalCapacity: getEmptyData(start, end) });
    const description = createErrorMessage(error);
    notification.error({
      className: 'notification-error',
      message: t('common.notifications.errorUpdatingAvailableCapacityImatra'),
      description,
    });
    log.error('Error updating available capacity for Imatra:', error.message, description);
  }
}

async function updateAvailableCapacityImatra(start, end) {
  try {
    const { body } = await API.get({
      apiName: 'FINTSO',
      path: '/admin/dashboard/capacity/available',
      options: {
        queryParams: {
          start,
          end,
        },
      },
    }).response;
    const availableCapacityImatra = await body.json();
    this.setState({ availableCapacityImatra });
    log.info('Available capacity for Imatra updated with data:', availableCapacityImatra);
  } catch (error) {
    const { t } = this.props;
    this.setState({ availableCapacityImatra: getEmptyData(start, end) });
    const description = createErrorMessage(error);
    notification.error({
      className: 'notification-error',
      message: t('common.notifications.errorUpdatingAvailableCapacityImatra'),
      description,
    });
    log.error('Error updating available capacity for Imatra:', error.message, description);
  }
}

async function updateAvailableCapacityBC(start, end) {
  const dates = getDatesInRange(start, end);
  try {
    const responses = await Promise.all(
      dates.map((date) => API.get({ apiName: 'FINTSO', path: `/admin/capacity/bc/${date}` }).response),
    );
    const responseBodies = await Promise.all(
      responses.map(({ body }) => body.json()),
    );
    const availableCapacityBC = responseBodies.reduce((result, { date, entry, exit }) => ({
      ...result,
      [date]: {
        entry,
        exit,
      },
    }),
    {});
    this.setState({ availableCapacityBC });
    log.info('Available capacity for BalticConnector updated with data:', availableCapacityBC);
  } catch (error) {
    const { t } = this.props;
    this.setState({ availableCapacityBC: getEmptyData(start, end) });
    const description = createErrorMessage(error);
    notification.error({
      className: 'notification-error',
      message: t('common.notifications.errorUpdatingAvailableCapacityBC'),
      description,
    });
    log.error('Error updating available capacity for Balticconnector:', error.message, description);
  }
}

async function updateBookedCapacity(start, end) {
  try {
    const { body } = await API.get({
      apiName: 'FINTSO',
      path: '/admin/dashboard/capacity/bookings',
      options: {
        queryParams: {
          start,
          end,
        },
      },
    }).response;
    const bookedCapacity = await body.json();
    this.setState({ bookedCapacity });
    log.info('Booked capacity updated with data:', bookedCapacity);
  } catch (error) {
    const { t } = this.props;
    this.setState({ bookedCapacity: getEmptyData(start, end) });
    const description = createErrorMessage(error);
    notification.error({
      className: 'notification-error',
      message: t('common.notifications.errorUpdatingBookedCapacity'),
      description,
    });
    log.error('Error updating booked capacity:', error.message, description);
  }
}

async function updateDailyAndHourlyCapacity(start, end) {
  try {
    const options = {
      queryParams: { start, end },
    };
    const { body } = await API.get({
      apiName: 'FINTSO',
      path: '/admin/dashboard/capacity/imatra',
      options,
    }).response;
    const { items } = await body.json();
    const dailyAndHourlyCapacity = items.reduce((result, { date, hourly, daily }) => ({
      ...result,
      [date]: {
        hourly,
        daily,
      },
    }),
    {});
    this.setState({ dailyAndHourlyCapacity });
    log.info('Daily and hourly capacity updated with data:', dailyAndHourlyCapacity);
  } catch (error) {
    const { t } = this.props;
    this.setState({ dailyAndHourlyCapacity: getEmptyData(start, end) });
    const description = createErrorMessage(error);
    notification.error({
      className: 'notification-error',
      message: t('common.notifications.errorUpdatingDailyAndHourlyCapacity'),
      description,
    });
    log.error('Error updating daily and hourly capacity:', error.message, description);
  }
}

async function updateNominations(start, end) {
  const options = {
    queryParams: {
      start,
      end,
    },
  };

  try {
    const { body: nomresBody } = await API.get({
      apiName: 'FINTSO',
      path: '/admin/dashboard/nominations/nomres',
      options,
    }).response;
    const nomres = await nomresBody.json();

    const { body: nomintBody } = await API.get({
      apiName: 'FINTSO',
      path: '/admin/dashboard/nominations/nomint',
      options,
    }).response;
    const nomint = await nomintBody.json();

    const nominations = {
      nomres,
      nomint,
    };
    this.setState({ nominations });
    log.info('Nominations updated with data:', nominations);
  } catch (error) {
    this.setState({
      nominations: {
        nomres: getEmptyData(start, end),
        nomint: getEmptyData(start, end),
      },
    });
    const { t } = this.props;
    const description = createErrorMessage(error);
    notification.error({
      className: 'notification-error',
      message: t('common.notifications.errorUpdatingNominations'),
      description,
    });
    log.error('Error updating nominations:', error.message, description);
  }
}

async function updatePhysicalBalanceSummary(start, end) {
  const options = {
    queryParams: {
      start,
      end,
    },
  };

  try {
    const { body } = await API.get({
      apiName: 'FINTSO',
      path: '/admin/dashboard/summary',
      options,
    }).response;
    const { items } = await body.json();
    const physicalBalanceSummary = groupBy(items, ({ gasDay }) => gasDay);
    this.setState({ physicalBalanceSummary });
    log.info('Physical balance summary updated with data:', physicalBalanceSummary);
  } catch (error) {
    const { t } = this.props;
    this.setState({ physicalBalanceSummary: getEmptyData(start, end) });
    const description = createErrorMessage(error);
    notification.error({
      className: 'notification-error',
      message: t('common.notifications.errorUpdatingPhysicalBalanceSummary'),
      description,
    });
    log.error('Error updating physical balance summary:', error.message, description);
  }
}

async function updateTotalCapacities(start, end, refresh = false) {
  const { totalCapacities } = this.state;
  const existing = Object.keys(totalCapacities);
  const dates = getDatesInRange(start, end).filter(date => refresh || !existing.includes(date));
  const url = '/admin/dashboard/capacity/bookings/by/marketparty';

  try {
    const responses = await Promise.all(dates.map(async (date) => {
      const { body } = await API.get({
        apiName: 'FINTSO',
        path: url,
        options: { queryParams: { date } },
      }).response;
      const response = await body.json();
      return { [date]: response };
    }));
    const combined = { ...totalCapacities, ...responses.reduce(merge, {}) };
    this.setState({ totalCapacities: combined });
    log.info('Total capacities updated with data:', combined);
  } catch (error) {
    const { t } = this.props;
    this.setState({ totalCapacities: getEmptyData(start, end) });
    const description = createErrorMessage(error);
    notification.error({
      className: 'notification-error',
      message: t('common.notifications.errorUpdatingTotalCapacities'),
      description,
    });
    log.error('Error updating total capacities:', error.message, description);
  }
}

const monitoringContext = {
  technicalCapacity: {},
  availableCapacityImatra: {},
  availableCapacityBC: {},
  bookedCapacity: {},
  dailyAndHourlyCapacity: {},
  totalCapacities: {},
  nominations: {},
  physicalBalanceSummary: {},
  updateTechnicalCapacity: () => { },
  updateAvailableCapacityImatra: () => { },
  updateAvailableCapacityBC: () => { },
  updateBookedCapacity: () => { },
  updateDailyAndHourlyCapacity: () => { },
  updateTotalCapacities: () => { },
  updateNominations: () => { },
  updatePhysicalBalanceSummary: () => { },
};

const functions = {
  updateTechnicalCapacity,
  updateAvailableCapacityImatra,
  updateAvailableCapacityBC,
  updateBookedCapacity,
  updateDailyAndHourlyCapacity,
  updateTotalCapacities,
  updateNominations,
  updatePhysicalBalanceSummary,
};

const monitoring = {
  ...monitoringContext,
  ...functions,
};

export {
  monitoringContext,
  monitoring,
};
